import React, { useState, useEffect, useMemo } from "react";
import {
  DollarSign, TrendingUp, Clock, FileText, Users, Filter,
  Download, AlertTriangle, CheckCircle, BarChart3, Calendar,
} from "lucide-react";
import { getBillingConfig, getShortCode } from "../../services/billingConfigService";

export default function BillingTab({ C, submittedNotes, allNotes, individuals, teamMembers, orgId }) {
  const [billingConfig, setBillingConfig] = useState(null);
  const [dateRange, setDateRange] = useState("month");
  const [groupBy, setGroupBy] = useState("individual");

  useEffect(() => {
    if (orgId) {
      getBillingConfig(orgId).then(setBillingConfig);
    }
  }, [orgId]);

  const codeFormat = billingConfig?.codeFormat || "hcpcs";

  // Filter notes by date range
  const filteredNotes = useMemo(() => {
    const now = new Date();
    let cutoff;
    if (dateRange === "week") {
      cutoff = new Date(now);
      cutoff.setDate(cutoff.getDate() - 7);
    } else if (dateRange === "month") {
      cutoff = new Date(now);
      cutoff.setMonth(cutoff.getMonth() - 1);
    } else if (dateRange === "quarter") {
      cutoff = new Date(now);
      cutoff.setMonth(cutoff.getMonth() - 3);
    } else {
      cutoff = new Date(0);
    }

    return submittedNotes.filter(n => {
      const d = n.createdAt?.toDate ? n.createdAt.toDate() : new Date(n.createdAt || n.date);
      return d >= cutoff;
    });
  }, [submittedNotes, dateRange]);

  const getUnits = (note) => {
    const raw = note.billingUnits || "";
    const match = raw.match(/(\d+)/);
    return match ? parseInt(match[1]) : 1;
  };

  const getServiceCode = (note) => {
    const st = note.serviceType || note.noteData?.header?.serviceType || "ICM Weekly Visit";
    if (billingConfig?.services?.[st]) {
      return getShortCode(st, billingConfig);
    }
    const bc = note.billingCode || note.noteData?.billingCode || "";
    if (bc) {
      const code = bc.split(" ")[0].split("—")[0].trim();
      if (code) return code;
    }
    return "H0039";
  };

  const getServiceLabel = (note) => {
    return note.serviceType || note.noteData?.header?.serviceType || "ICM Weekly Visit";
  };

  // Aggregate stats
  const totalUnits = filteredNotes.reduce((s, n) => s + getUnits(n), 0);
  const totalNotes = filteredNotes.length;
  const unsignedCount = filteredNotes.filter(n => !n.supervisorReviewed).length;
  const flaggedCount = filteredNotes.filter(n => n.hasPriorityFlags).length;
  const avgUnitsPerNote = totalNotes > 0 ? (totalUnits / totalNotes).toFixed(1) : "0";

  // Service type breakdown
  const byService = useMemo(() => {
    const map = {};
    filteredNotes.forEach(n => {
      const label = getServiceLabel(n);
      const code = getServiceCode(n);
      const units = getUnits(n);
      if (!map[label]) map[label] = { code, count: 0, units: 0 };
      map[label].count++;
      map[label].units += units;
    });
    return Object.entries(map).sort((a, b) => b[1].units - a[1].units);
  }, [filteredNotes, billingConfig]);

  // By individual
  const byIndividual = useMemo(() => {
    const map = {};
    filteredNotes.forEach(n => {
      const name = n.individualName || "Unknown";
      const units = getUnits(n);
      const svc = getServiceLabel(n);
      const code = getServiceCode(n);
      const signed = n.supervisorReviewed ? 1 : 0;
      if (!map[name]) map[name] = { count: 0, units: 0, signed: 0, services: {} };
      map[name].count++;
      map[name].units += units;
      map[name].signed += signed;
      if (!map[name].services[svc]) map[name].services[svc] = { code, count: 0, units: 0 };
      map[name].services[svc].count++;
      map[name].services[svc].units += units;
    });
    return Object.entries(map).sort((a, b) => b[1].units - a[1].units);
  }, [filteredNotes, billingConfig]);

  // By agent
  const byAgent = useMemo(() => {
    const map = {};
    filteredNotes.forEach(n => {
      const name = n.agentName || "Unknown Agent";
      const units = getUnits(n);
      const signed = n.supervisorReviewed ? 1 : 0;
      if (!map[name]) map[name] = { count: 0, units: 0, signed: 0 };
      map[name].count++;
      map[name].units += units;
      map[name].signed += signed;
    });
    return Object.entries(map).sort((a, b) => b[1].units - a[1].units);
  }, [filteredNotes]);

  // Daily breakdown (14 days)
  const dailyData = useMemo(() => {
    const days = [];
    const now = new Date();
    for (let i = 13; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dateStr = d.toLocaleDateString();
      const dayNotes = filteredNotes.filter(n => {
        const nd = n.createdAt?.toDate ? n.createdAt.toDate() : new Date(n.createdAt || n.date);
        return nd.toLocaleDateString() === dateStr;
      });
      const units = dayNotes.reduce((s, n) => s + getUnits(n), 0);
      days.push({
        short: d.toLocaleDateString("en-US", { month: "numeric", day: "numeric" }),
        notes: dayNotes.length,
        units,
      });
    }
    return days;
  }, [filteredNotes]);

  const maxDailyUnits = Math.max(...dailyData.map(d => d.units), 1);

  // CSV export
  const handleExportCSV = () => {
    const headers = ["Date", "Individual", "Agent", "Service Type", "Billing Code", "Units", "Status", "Supervisor Signed", "Priority Flags"];
    const rows = filteredNotes.map(n => [
      n.date || "",
      n.individualName || "",
      n.agentName || "",
      getServiceLabel(n),
      getServiceCode(n),
      getUnits(n),
      n.status || "",
      n.supervisorReviewed ? "Yes" : "No",
      n.hasPriorityFlags ? "Yes" : "No",
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `billing-export-${dateRange}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {/* Date Range & Export */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {[
            { value: "week", label: "7 Days" },
            { value: "month", label: "30 Days" },
            { value: "quarter", label: "90 Days" },
            { value: "all", label: "All Time" },
          ].map(opt => (
            <button key={opt.value} onClick={() => setDateRange(opt.value)} style={{
              padding: "5px 12px", borderRadius: 8, fontSize: 10, fontWeight: 700, cursor: "pointer",
              background: dateRange === opt.value ? "rgba(49,130,206,0.2)" : "rgba(15,23,42,0.5)",
              color: dateRange === opt.value ? "#63b3ed" : "#718096",
              border: `1px solid ${dateRange === opt.value ? "rgba(49,130,206,0.3)" : "rgba(99,179,237,0.06)"}`,
            }}>{opt.label}</button>
          ))}
        </div>
        <button onClick={handleExportCSV} style={{
          display: "flex", alignItems: "center", gap: 4,
          padding: "5px 12px", borderRadius: 8, fontSize: 10, fontWeight: 700, cursor: "pointer",
          background: "rgba(104,211,145,0.08)", color: "#68d391",
          border: "1px solid rgba(104,211,145,0.15)",
        }}>
          <Download size={11} /> Export CSV
        </button>
      </div>

      {/* Top Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginBottom: 16 }}>
        {[
          { label: "Total Notes", value: totalNotes, sub: `${unsignedCount} unsigned`, icon: FileText, color: "#4fd1c5" },
          { label: "Total Units", value: totalUnits, sub: `avg ${avgUnitsPerNote}/note`, icon: BarChart3, color: "#63b3ed" },
          { label: "Flagged Notes", value: flaggedCount, sub: "require review", icon: AlertTriangle, color: flaggedCount > 0 ? "#fc8181" : "#68d391" },
          { label: "Co-Signed", value: totalNotes - unsignedCount, sub: `of ${totalNotes} notes`, icon: CheckCircle, color: "#68d391" },
        ].map((s, i) => (
          <div key={i} style={{ ...C.card, padding: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
              <div>
                <div style={{ fontSize: 8, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1 }}>{s.label}</div>
                <div style={{ fontSize: 26, fontWeight: 900, color: s.color, marginTop: 4, fontFamily: "monospace" }}>{s.value}</div>
                <div style={{ fontSize: 9, color: "#4a5568", marginTop: 2 }}>{s.sub}</div>
              </div>
              <s.icon size={16} style={{ color: s.color, opacity: 0.4 }} />
            </div>
          </div>
        ))}
      </div>

      {/* Daily Activity Chart */}
      <div style={{ ...C.card, marginBottom: 16, padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 800 }}>Daily Billing Units</div>
          <div style={{ fontSize: 9, color: "#718096" }}>Last 14 days</div>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 80 }}>
          {dailyData.map((d, i) => (
            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ fontSize: 8, color: "#63b3ed", fontWeight: 700, marginBottom: 2 }}>
                {d.units > 0 ? d.units : ""}
              </div>
              <div style={{
                width: "100%", borderRadius: "4px 4px 0 0",
                height: Math.max(2, (d.units / maxDailyUnits) * 60),
                background: d.units > 0
                  ? "linear-gradient(to top, rgba(99,179,237,0.3), rgba(99,179,237,0.6))"
                  : "rgba(99,179,237,0.06)",
              }} />
              <div style={{ fontSize: 7, color: "#4a5568", marginTop: 3, whiteSpace: "nowrap" }}>{d.short}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Service Type Breakdown */}
      <div style={{ ...C.card, marginBottom: 16, padding: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 12 }}>Billing by Service Type</div>
        {byService.length === 0 ? (
          <div style={{ textAlign: "center", padding: "20px 0", fontSize: 12, color: "#4a5568" }}>No data</div>
        ) : (
          byService.map(([label, data], i) => {
            const pct = totalUnits > 0 ? Math.round((data.units / totalUnits) * 100) : 0;
            return (
              <div key={label} style={{ marginBottom: i < byService.length - 1 ? 12 : 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{
                      fontSize: 9, fontWeight: 800, fontFamily: "monospace", padding: "2px 6px",
                      borderRadius: 4, background: "rgba(79,209,197,0.1)", color: "#4fd1c5",
                    }}>{data.code}</span>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{label}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontSize: 10, color: "#718096" }}>{data.count} notes</span>
                    <span style={{ fontSize: 12, fontWeight: 800, fontFamily: "monospace", color: "#63b3ed" }}>{data.units} units</span>
                  </div>
                </div>
                <div style={{ height: 4, borderRadius: 2, background: "rgba(99,179,237,0.06)" }}>
                  <div style={{
                    height: "100%", borderRadius: 2, width: `${pct}%`,
                    background: "linear-gradient(90deg, #3182ce, #4fd1c5)",
                  }} />
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Group Toggle */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {[
          { value: "individual", label: "By Individual" },
          { value: "agent", label: "By Agent" },
        ].map(opt => (
          <button key={opt.value} onClick={() => setGroupBy(opt.value)} style={{
            display: "flex", alignItems: "center", gap: 4,
            padding: "6px 14px", borderRadius: 8, fontSize: 10, fontWeight: 700, cursor: "pointer",
            background: groupBy === opt.value ? "rgba(49,130,206,0.15)" : "transparent",
            color: groupBy === opt.value ? "#63b3ed" : "#718096",
            border: `1px solid ${groupBy === opt.value ? "rgba(49,130,206,0.2)" : "rgba(99,179,237,0.06)"}`,
          }}>
            <Users size={11} /> {opt.label}
          </button>
        ))}
      </div>

      {/* By Individual */}
      {groupBy === "individual" && (
        <div style={C.card}>
          <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 12 }}>Individual Billing Detail</div>
          {byIndividual.length === 0 ? (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <DollarSign size={24} style={{ color: "#2d3748", margin: "0 auto 8px" }} />
              <div style={{ fontSize: 12, color: "#4a5568" }}>No billing data</div>
            </div>
          ) : (
            byIndividual.map(([name, data], i) => (
              <div key={name} style={{
                padding: "10px 0",
                borderBottom: i < byIndividual.length - 1 ? "1px solid rgba(99,179,237,0.06)" : "none",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{name}</div>
                    <div style={{ fontSize: 10, color: "#718096", marginTop: 2 }}>
                      {data.count} notes · {data.signed}/{data.count} co-signed
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 16, fontWeight: 900, color: "#4fd1c5", fontFamily: "monospace" }}>{data.units}</div>
                    <div style={{ fontSize: 8, color: "#4a5568", textTransform: "uppercase" }}>units</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                  {Object.entries(data.services).map(([svc, sd]) => (
                    <span key={svc} style={{
                      fontSize: 9, padding: "2px 8px", borderRadius: 6,
                      background: "rgba(99,179,237,0.06)", color: "#a0aec0",
                    }}>
                      <strong style={{ color: "#63b3ed" }}>{sd.code}</strong> {sd.count}× · {sd.units}u
                    </span>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* By Agent */}
      {groupBy === "agent" && (
        <div style={C.card}>
          <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 12 }}>Agent Productivity</div>
          {byAgent.length === 0 ? (
            <div style={{ textAlign: "center", padding: "24px 0", fontSize: 12, color: "#4a5568" }}>No data</div>
          ) : (
            byAgent.map(([name, data], i) => {
              const signRate = data.count > 0 ? Math.round((data.signed / data.count) * 100) : 0;
              return (
                <div key={name} style={{
                  padding: "10px 0",
                  borderBottom: i < byAgent.length - 1 ? "1px solid rgba(99,179,237,0.06)" : "none",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{name}</div>
                      <div style={{ fontSize: 10, color: "#718096", marginTop: 2 }}>
                        {data.count} notes · {signRate}% co-signed
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 16, fontWeight: 900, color: "#63b3ed", fontFamily: "monospace" }}>{data.units}</div>
                      <div style={{ fontSize: 8, color: "#4a5568", textTransform: "uppercase" }}>units</div>
                    </div>
                  </div>
                  <div style={{ height: 3, borderRadius: 2, background: "rgba(99,179,237,0.06)", marginTop: 6 }}>
                    <div style={{
                      height: "100%", borderRadius: 2, width: `${signRate}%`,
                      background: signRate === 100 ? "#68d391" : signRate > 50 ? "#ecc94b" : "#fc8181",
                    }} />
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Unsigned Warning */}
      {unsignedCount > 0 && (
        <div style={{
          ...C.card, marginTop: 16,
          background: "rgba(252,129,129,0.03)",
          border: "1px solid rgba(252,129,129,0.12)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <AlertTriangle size={14} style={{ color: "#fc8181" }} />
            <span style={{ fontSize: 12, fontWeight: 800, color: "#fc8181" }}>Unsigned Notes</span>
          </div>
          <div style={{ fontSize: 11, color: "#a0aec0", lineHeight: 1.5 }}>
            {unsignedCount} note{unsignedCount !== 1 ? "s" : ""} awaiting supervisor co-sign.
            Notes without co-signature cannot be submitted for Medicaid billing.
          </div>
        </div>
      )}

      {/* Config Info */}
      {billingConfig && (
        <div style={{
          ...C.card, marginTop: 16,
          background: "rgba(79,209,197,0.03)",
          border: "1px solid rgba(79,209,197,0.1)",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#4fd1c5", marginBottom: 4 }}>Billing Configuration</div>
              <div style={{ fontSize: 10, color: "#718096" }}>
                Code format: <strong style={{ color: "#a0aec0" }}>{codeFormat.toUpperCase()}</strong> ·
                Unit size: <strong style={{ color: "#a0aec0" }}>{billingConfig.unitMinutes || 15} min</strong> ·
                Auto-calc: <strong style={{ color: "#a0aec0" }}>{billingConfig.autoCalculateUnits ? "On" : "Off"}</strong>
              </div>
            </div>
            <div style={{ fontSize: 9, color: "#4a5568" }}>Settings → Billing</div>
          </div>
        </div>
      )}
    </div>
  );
}